/**
 * DIFOT dashboard — shared types, constants and pure helpers.
 *
 * The Worker's /api/reports/difot endpoint returns rows from the
 * `v_difot_dashboard` Postgres view in this exact (terse) shape.
 */

export interface RawRow {
  id: number;
  ref: string;
  so: string | null;
  st: string;
  s: string | null;
  d: string; // trip date (YYYY-MM-DD)
  dr: string | null; // driver
  tm: string | null; // team / vehicle
  a: string | null; // address
  q: number | string | null; // item quantity
  et: string | null; // enroute time
  at: string | null; // arrived time
  ct: string | null; // completed time
  ft: string | null; // failed time
  sd: number | string | null; // service minutes
  td: number | string | null; // travel minutes
  dk: number | string | null; // distance km
  ot: number | string | null; // on time (1/0)
  fn: string | null; // failure note
  r: string | null; // metro zone
}

export interface Row {
  id: number;
  ref: string;
  so: string | null;
  st: string;
  s: string | null;
  d: string;
  dr: string | null;
  tm: string | null;
  a: string | null;
  q: number;
  et: string | null;
  at: string | null;
  ct: string | null;
  ft: string | null;
  sd: number | null;
  td: number | null;
  dk: number | null;
  ot: boolean | null;
  fn: string | null;
  r: string | null;
}

function num(v: number | string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function coerceRow(r: RawRow): Row {
  return {
    ...r,
    q: num(r.q) ?? 0,
    sd: num(r.sd),
    td: num(r.td),
    dk: num(r.dk),
    ot: r.ot === 1 || r.ot === '1' ? true : r.ot === 0 || r.ot === '0' ? false : null,
  };
}

export type Period = 'daily' | 'weekly' | 'monthly';
export type DateRange = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'custom';

/** Preset chips shown in the dashboard date filter, in display order. */
export const DATE_PRESETS: { key: DateRange; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

export const ZONE_COLOURS: Record<string, string> = {
  CBD: '#003D6C',
  'Inner West': '#0061A2',
  'Inner East': '#1A85C9',
  North: '#0BDA8E',
  'South East': '#B37D6C',
  'Outer Metro': '#384553',
  Mornington: '#FF9500',
  'Geelong / Outer W': '#FFD200',
  'North Country': '#7FB5D9',
  'West Country': '#C5CFDD',
  Gippsland: '#FF5245',
  'Goulburn Valley': '#FFB347',
  'North East': '#9C6AE5',
  Other: '#737474',
};

export const STATUS_COLOURS: Record<string, string> = {
  Complete: '#0BDA8E',
  Pending: '#B37D6C',
  Failed: '#FF5245',
  Cancelled: '#FF5245',
  Enroute: '#4ED2FF',
  Arrived: '#2B93FF',
};

export const ZONE_ORDER = [
  'CBD',
  'Inner West',
  'Inner East',
  'North',
  'South East',
  'Outer Metro',
  'Mornington',
  'Geelong / Outer W',
  'North Country',
  'West Country',
  'Gippsland',
  'Goulburn Valley',
  'North East',
  'Other',
];

export interface FailCategory {
  key: string;
  label: string;
  color: string;
  patterns: RegExp[];
}

export const FAIL_CATEGORIES: FailCategory[] = [
  {
    key: 'customer_unavailable',
    label: 'Customer unavailable',
    color: '#B37D6C',
    patterns: [
      /not home/i,
      /no answer/i,
      /no one home/i,
      /switched off/i,
      /didn.?t answer/i,
      /not avail/i,
      /couldn.?t get a hold/i,
      /no.?one was home/i,
      /not there/i,
      /wasn.?t at home/i,
      /no information provided/i,
    ],
  },
  {
    key: 'reschedule',
    label: 'Rescheduled / cancelled',
    color: '#FF9500',
    patterns: [
      /reschedule/i,
      /re ?schedule/i,
      /cancel/i,
      /didn.?t book/i,
      /not ready/i,
      /later date/i,
      /lunch time/i,
      /will be delivered at a later/i,
      /needs? to re ?schedule/i,
      /refer to store/i,
      /store called/i,
    ],
  },
  {
    key: 'damage',
    label: 'Damage / refused',
    color: '#FF5245',
    patterns: [/damage/i, /refused/i, /dust inside/i, /swap.?over/i, /not happy/i, /return/i, /broken/i],
  },
  {
    key: 'access',
    label: 'Access / loading',
    color: '#0061A2',
    patterns: [/loading/i, /dock/i, /access/i, /can.?t get in/i, /no parking/i, /gate/i, /locked/i, /waited.*more than/i],
  },
  {
    key: 'address',
    label: 'Address / location',
    color: '#1A85C9',
    patterns: [/wrong address/i, /can.?t find/i, /address.*not/i, /no such/i, /incorrect address/i],
  },
  { key: 'other', label: 'Other / uncategorised', color: '#737474', patterns: [] },
];

export function categoriseFailure(note: string | null): string {
  if (!note || !note.trim()) return 'other';
  for (const c of FAIL_CATEGORIES) {
    if (c.key === 'other') continue;
    for (const p of c.patterns) if (p.test(note)) return c.key;
  }
  return 'other';
}

// ── Date / time helpers ───────────────────────────────────────────

/**
 * Trip dates (`d`) are plain calendar dates (`YYYY-MM-DD`) and the whole
 * dashboard reports in Australia/Melbourne time, so all range maths is done on
 * the string form to avoid the classic `new Date('YYYY-MM-DD')` UTC-midnight
 * drift that shifted "yesterday" by a day depending on the viewer's timezone.
 */

/** Today's date in Australia/Melbourne as `YYYY-MM-DD`. */
export function melbourneToday(): string {
  // en-CA renders as YYYY-MM-DD; the timeZone option pins it to Melbourne.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Melbourne',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Shift a `YYYY-MM-DD` string by `days` (may be negative), timezone-safe. */
export function shiftDate(date: string, days: number): string {
  const ms = Date.parse(date + 'T00:00:00Z') + days * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

export interface DateBounds {
  from: string | null; // inclusive YYYY-MM-DD, or null for open-ended
  to: string | null;
}

/** Resolve a preset (or custom from/to) into inclusive `YYYY-MM-DD` bounds. */
export function rangeBounds(
  range: DateRange,
  opts: { from?: string; to?: string; today?: string } = {},
): DateBounds {
  const today = opts.today ?? melbourneToday();
  switch (range) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = shiftDate(today, -1);
      return { from: y, to: y };
    }
    case 'last7':
      return { from: shiftDate(today, -6), to: today };
    case 'last30':
      return { from: shiftDate(today, -29), to: today };
    case 'thisMonth':
      return { from: today.slice(0, 8) + '01', to: today };
    case 'custom':
      return { from: opts.from || null, to: opts.to || null };
    case 'all':
    default:
      return { from: null, to: null };
  }
}

/** True when a trip date falls within the bounds. Null/blank dates never match. */
export function inBounds(d: string | null, b: DateBounds): boolean {
  if (!d) return false;
  if (b.from && d < b.from) return false;
  if (b.to && d > b.to) return false;
  return true;
}

/** Latest non-null calendar date across rows, or null when none have a date. */
export function latestDate(rows: { d: string | null }[]): string | null {
  let max: string | null = null;
  for (const r of rows) if (r.d && (max === null || r.d > max)) max = r.d;
  return max;
}

/** Human label for a resolved window, e.g. "Thu 28 May 2026" or a range. */
export function formatBoundsLabel(b: DateBounds): string {
  if (!b.from && !b.to) return 'All available dates';
  if (b.from && b.to) {
    return b.from === b.to ? formatDateAU(b.from) : `${formatDateAU(b.from)} – ${formatDateAU(b.to)}`;
  }
  if (b.from) return `From ${formatDateAU(b.from)}`;
  return `Up to ${formatDateAU(b.to)}`;
}

export function formatDateAU(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Melbourne' });
}

export function formatTimeAU(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Melbourne' });
}

export function periodKey(dateStr: string, period: Period): string {
  const d = new Date(dateStr);
  if (period === 'daily') return dateStr;
  if (period === 'weekly') {
    const day = d.getUTCDay() || 7;
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() - day + 1);
    return monday.toISOString().slice(0, 10);
  }
  if (period === 'monthly') return dateStr.slice(0, 7) + '-01';
  return dateStr;
}

export function formatPeriodLabel(key: string, period: Period): string {
  const d = new Date(key);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (period === 'daily') return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  if (period === 'weekly') {
    const end = new Date(d);
    end.setUTCDate(end.getUTCDate() + 6);
    return 'Wk ' + d.toLocaleDateString('en-AU', opts) + ' – ' + end.toLocaleDateString('en-AU', opts);
  }
  if (period === 'monthly') return d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  return key;
}

export interface Metrics {
  total: number;
  completed: number;
  failed: number;
  pending: number;
  cancelled: number;
  denom: number;
  difot: number;
  difotPct: number;
  onTimePct: number;
  completionPct: number;
  failurePct: number;
  drivers: number;
  avgService: number;
}

export function computeMetrics(rows: Row[]): Metrics {
  const total = rows.length;
  const completed = rows.filter((r) => r.st === 'Complete');
  const failed = rows.filter((r) => r.st === 'Failed');
  const pending = rows.filter((r) => r.st === 'Pending' || r.st === 'Enroute' || r.st === 'Arrived');
  const cancelled = rows.filter((r) => r.st === 'Cancelled');

  // DIFOT: Delivered (Complete) + On Time (ot === true). Denominator is every
  // non-cancelled scheduled stop. "In full" is implied by completion — the
  // source data does not track partial deliveries (item_quantity is always 0).
  const denom = rows.filter((r) => r.st !== 'Cancelled').length;
  const difot = rows.filter((r) => r.st === 'Complete' && r.ot === true).length;
  const onTimePct = completed.length ? (completed.filter((r) => r.ot === true).length / completed.length) * 100 : 0;
  const completionPct = denom ? (completed.length / denom) * 100 : 0;
  const failurePct = denom ? (failed.length / denom) * 100 : 0;
  const difotPct = denom ? (difot / denom) * 100 : 0;
  const drivers = new Set(rows.map((r) => r.dr).filter(Boolean));
  const services = completed.map((r) => r.sd).filter((v): v is number => v != null && v >= 0 && v < 240);
  const avgService = services.length ? services.reduce((a, b) => a + b, 0) / services.length : 0;
  return {
    total,
    completed: completed.length,
    failed: failed.length,
    pending: pending.length,
    cancelled: cancelled.length,
    denom,
    difot,
    difotPct,
    onTimePct,
    completionPct,
    failurePct,
    drivers: drivers.size,
    avgService,
  };
}

export function difotTone(pct: number): string {
  return pct >= 95 ? '#0BDA8E' : pct >= 85 ? '#FF9500' : '#FF5245';
}
